'use client';

import { Box, Stack, SimpleGrid, Text } from '@chakra-ui/react';
import { NestedHome, NestedUser } from '@habity/types';
import { HomeCard } from '../../../../ui/cards/HomeCard';
import { useTranslations } from 'next-intl';
import { RelatedHomesSkeletonLoadingGrid } from '~/components/homes/profile/components/related/RelatedHomesSkeleton';
import { getHomePath, isAddedToWishlist } from '@habity/services';
import { InfiniteScrollTrigger } from '~/components/ui/scroll/InfiniteScrollTrigger';
import { placeholderHomes } from '@habity/constants';
import { useMemo } from 'react';
import { useGridFullRows } from '~/hooks/useGridFullRows';

interface RelatedHomesProps {
  relatedHomes: NestedHome[];
  relatedHomesLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  user?: NestedUser | null;
}

export const RelatedHomes = ({
  relatedHomes,
  relatedHomesLoading,
  hasMore,
  loadMore,
  user,
}: RelatedHomesProps) => {
  const t = useTranslations('HomeProfile.RelatedHomes');

  const homes = useMemo(() => {
    if (relatedHomesLoading && (!relatedHomes || relatedHomes.length === 0)) {
      return placeholderHomes;
    } else if (relatedHomes && relatedHomes.length > 0) {
      return relatedHomes;
    } else {
      return [];
    }
  }, [relatedHomes, relatedHomesLoading]);

  const { gridRef, visibleItems: visibleHomes } = useGridFullRows(
    homes,
    hasMore
  );

  return (
    <Stack spacing="8">
      <Text size="sm" fontWeight="light" color="blackAlpha.800">
        {t('title')}
      </Text>

      {relatedHomesLoading && (!relatedHomes || relatedHomes.length === 0) ? (
        <RelatedHomesSkeletonLoadingGrid />
      ) : !visibleHomes.length ? (
        <Text fontStyle="italic" color="blackAlpha.600">
          Inga relaterade hem hittades
        </Text>
      ) : (
        <Stack>
          <SimpleGrid
            ref={gridRef}
            columns={{ mobile: 1, tablet: 2, desktop: 3 }}
            rowGap="10"
            columnGap="5"
          >
            {visibleHomes.map((home: NestedHome, index: number) => {
              const addedToWishlist = user
                ? isAddedToWishlist(user, home.id)
                : false;

              return (
                <HomeCard
                  key={home.id || index}
                  href={getHomePath(home)}
                  home={home}
                  user={user || undefined}
                  addedToWishlist={addedToWishlist}
                />
              );
            })}

            {relatedHomesLoading && relatedHomes && relatedHomes.length > 0 && (
              <>
                {(() => {
                  const ITEMS_PER_ROW = 3;
                  const visibleCount = visibleHomes.length;
                  const remainder = visibleCount % ITEMS_PER_ROW;
                  const skeletonsNeeded =
                    remainder === 0 ? ITEMS_PER_ROW : ITEMS_PER_ROW - remainder;

                  return [...Array(skeletonsNeeded)].map((_, index) => (
                    <Box key={`loading-skeleton-${index}`}>
                      <HomeCard
                        href=""
                        home={placeholderHomes[0]}
                        isLoading={true}
                      />
                    </Box>
                  ));
                })()}
              </>
            )}
          </SimpleGrid>

          <InfiniteScrollTrigger
            onVisible={loadMore}
            isLoading={relatedHomesLoading}
            hasMore={hasMore}
            infiniteScrollEnabled={true}
            showCount={false}
            results={visibleHomes.length}
            total={relatedHomes?.[0]?.count || 0}
            buttonText={t('loadMore')}
            boxProps={{ mt: '20' }}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default RelatedHomes;
